'use client';

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import * as Dialog from '@radix-ui/react-dialog';
import { BodyTextMediumB } from '@/shared/ui/typography2';
import { useScopedI18n } from '@locales/client';
import { NavigationGuardContext } from './navigation-guard-context';
import {
  Buttons,
  CancelButton,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogTitle,
  ExitButton,
  TextContainer,
} from './styles';

export function NavigationGuardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const needsConfirmationRef = useRef(false);
  const setNeedsConfirmation = useCallback((value: boolean) => {
    needsConfirmationRef.current = value;
  }, []);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const t = useScopedI18n('common.game.dockbar');

  const requestNavigation = useCallback(
    (url: string) => {
      if (needsConfirmationRef.current) {
        setPendingUrl(url);
        setDialogOpen(true);
      } else {
        router.push(url);
      }
    },
    [router]
  );

  const contextValue = useMemo(
    () => ({
      setNeedsConfirmation,
      requestNavigation,
    }),
    [setNeedsConfirmation, requestNavigation]
  );

  const cancelNavigation = () => {
    setDialogOpen(false);
    setPendingUrl(null);
  };

  const confirmNavigation = () => {
    if (pendingUrl) router.push(pendingUrl);
    cancelNavigation();
  };

  return (
    <NavigationGuardContext.Provider value={contextValue}>
      {children}

      <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
        <Dialog.Portal>
          <DialogOverlay />
          <DialogContent aria-describedby="" data-item="leave_game_confirm">
            <TextContainer>
              <DialogTitle>{t('exit_request_title')}</DialogTitle>
              <DialogDescription color="#AFAFAF">
                {t('exit_request_description')}
              </DialogDescription>
            </TextContainer>
            <Buttons>
              <ExitButton
                onClick={confirmNavigation}
                data-analytics-id="leave_game_confirm"
              >
                <BodyTextMediumB>{t('exit_request_confirm')}</BodyTextMediumB>
              </ExitButton>
              <Dialog.Close asChild>
                <CancelButton onClick={cancelNavigation}>
                  <BodyTextMediumB>{t('exit_request_cancel')}</BodyTextMediumB>
                </CancelButton>
              </Dialog.Close>
            </Buttons>
          </DialogContent>
        </Dialog.Portal>
      </Dialog.Root>
    </NavigationGuardContext.Provider>
  );
}
